import { getLocale, useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModelUpdate } from '@rhino-project/core/hooks';
import { Icon } from '@iconify/react';
import { DatePicker, DateField, Calendar, Typography } from '@heroui/react';
import {
  getLocalTimeZone,
  now,
  parseAbsoluteToLocal,
  type ZonedDateTime
} from '@internationalized/date';

type LineupRecord = {
  id: number | string;
  name?: string | null;
  event_at?: string | null;
  kind?: string | null;
};

type InlineEditLineupHeaderProps = {
  lineup: LineupRecord;
  isAdmin: boolean;
  onSaved?: () => void;
};

const parseEventDate = (iso?: string | null) => {
  if (!iso) return null;
  try {
    return parseAbsoluteToLocal(iso);
  } catch {
    return null;
  }
};

const isSameInstant = (a: string | null, b: string | null) => {
  if (a === b) return true;
  if (!a || !b) return false;
  return new Date(a).getTime() === new Date(b).getTime();
};

const InlineEditLineupHeader = ({
  lineup,
  isAdmin,
  onSaved
}: InlineEditLineupHeaderProps) => {
  const { t } = useTranslation();

  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);
  const { mutate: updateLineup } = useModelUpdate('lineup');

  const placeholderValue = useMemo(
    () => now(getLocalTimeZone()).set({ millisecond: 0 }),
    []
  );

  const eventDateValue = useMemo(
    () => parseEventDate(lineup.event_at),
    [lineup.event_at]
  );

  const startEditingName = useCallback(() => {
    if (!isAdmin) return;
    setName(lineup.name ?? '');
    setEditingName(true);
  }, [isAdmin, lineup.name]);

  useEffect(() => {
    if (editingName && nameRef.current) nameRef.current.focus();
  }, [editingName]);

  const saveName = useCallback(() => {
    const trimmed = name.trim();
    if (!trimmed) {
      setEditingName(false);
      return;
    }
    if (trimmed !== (lineup.name ?? '')) {
      updateLineup(
        { id: lineup.id, name: trimmed } as Parameters<typeof updateLineup>[0],
        { onSuccess: onSaved }
      );
    }
    setEditingName(false);
  }, [name, lineup.id, lineup.name, updateLineup, onSaved]);

  const cancelName = useCallback(() => setEditingName(false), []);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') saveName();
      if (e.key === 'Escape') cancelName();
    },
    [saveName, cancelName]
  );

  const handleDateChange = useCallback(
    (value: ZonedDateTime | null) => {
      const newIso = value ? value.toAbsoluteString() : null;
      if (isSameInstant(newIso, lineup.event_at ?? null)) return;
      updateLineup(
        { id: lineup.id, event_at: newIso } as Parameters<
          typeof updateLineup
        >[0],
        { onSuccess: onSaved }
      );
    },
    [lineup.id, lineup.event_at, updateLineup, onSaved]
  );

  return (
    <div className="flex flex-col items-center gap-1">
      {editingName ? (
        <div className="flex items-center gap-2">
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleNameKeyDown}
            placeholder={t('frontend.messages.name')}
            className="font-bold text-2xl bg-transparent border-b-2 border-accent/50 focus:border-accent outline-none w-64 py-0"
          />
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              saveName();
            }}
            className="p-1 text-success hover:text-success/80"
          >
            <Icon icon="bi:check-lg" width="20" />
          </button>
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault();
              cancelName();
            }}
            className="p-1 text-muted hover:text-danger"
          >
            <Icon icon="bi:x-lg" width="18" />
          </button>
        </div>
      ) : (
        <div
          className="group flex items-center gap-2 cursor-default"
          onClick={startEditingName}
        >
          <Typography.Heading
            level={1}
            className={`m-0 ${isAdmin ? 'cursor-pointer' : ''}`}
          >
            {lineup.name}
          </Typography.Heading>
          {isAdmin && (
            <button
              type="button"
              className="p-1 text-muted opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
            >
              <Icon icon="bi:pencil" width="14" />
            </button>
          )}
        </div>
      )}

      {lineup.kind === 'depth_chart' ? null : isAdmin ? (
        <DatePicker
          granularity="minute"
          hideTimeZone
          placeholderValue={placeholderValue}
          value={eventDateValue}
          onChange={(v) => handleDateChange(v as ZonedDateTime | null)}
          aria-label={t('frontend.messages.event_date_and_time')}
          className="text-sm"
        >
          <DateField.Group>
            <DateField.Input>
              {(segment) => <DateField.Segment segment={segment} />}
            </DateField.Input>
            <DateField.Suffix>
              <DatePicker.Trigger>
                <DatePicker.TriggerIndicator />
              </DatePicker.Trigger>
            </DateField.Suffix>
          </DateField.Group>
          <DatePicker.Popover className="min-w-80">
            <Calendar>
              <Calendar.Header>
                <Calendar.YearPickerTrigger>
                  <Calendar.YearPickerTriggerHeading />
                  <Calendar.YearPickerTriggerIndicator />
                </Calendar.YearPickerTrigger>
                <Calendar.NavButton slot="previous" />
                <Calendar.NavButton slot="next" />
              </Calendar.Header>
              <Calendar.Grid>
                <Calendar.GridHeader>
                  {(day: string) => (
                    <Calendar.HeaderCell>{day}</Calendar.HeaderCell>
                  )}
                </Calendar.GridHeader>
                <Calendar.GridBody>
                  {(date) => <Calendar.Cell date={date} />}
                </Calendar.GridBody>
              </Calendar.Grid>
            </Calendar>
          </DatePicker.Popover>
        </DatePicker>
      ) : (
        lineup.event_at && (
          <span className="text-sm text-muted">
            {new Date(lineup.event_at).toLocaleString(
              getLocale().replaceAll('_', '-'),
              {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              }
            )}
          </span>
        )
      )}
    </div>
  );
};

export default InlineEditLineupHeader;
